import { useState } from "react";
import { Plus, Trash, ArrowUp, ArrowDown, Image as ImageIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

// Available data fields that can be mapped to card positions
const DATA_FIELDS = [
  { id: "club_name", label: "Club Name", sample: "Example Club" },
  { id: "member_name", label: "Member Name", sample: "First Last" },
  { id: "member_id", label: "Member ID", sample: "M-1001" },
  { id: "member_email", label: "Email", sample: "member@example.com" },
  { id: "membership_status", label: "Status", sample: "Active" },
  { id: "expiry_date", label: "Expiry Date", sample: "2027-03-31" },
  { id: "member_photo", label: "Member Photo", sample: "", isImage: true },
];

const FIELD_OPTIONS = DATA_FIELDS.filter((f) => !f.isImage);
const NONE_VALUE = "__none__";

const DEFAULT_LAYOUT = {
  header: "member_name",
  subheader: "membership_status",
  rows: [
    { items: [{ field: "member_photo", type: "image" }] },
    { items: [{ field: "member_id", type: "text" }, { field: "membership_status", type: "text" }] },
    { items: [{ field: "member_email", type: "text" }, { field: "expiry_date", type: "text" }] },
  ],
};

const getFieldLabel = (fieldId) => {
  const f = DATA_FIELDS.find((d) => d.id === fieldId);
  return f ? f.label : fieldId;
};

const getSampleValue = (fieldId) => {
  const f = DATA_FIELDS.find((d) => d.id === fieldId);
  return f ? f.sample : "";
};

const CardBuilder = ({ layout, onChange, design }) => {
  const currentLayout = layout || DEFAULT_LAYOUT;

  const update = (changes) => {
    onChange({ ...currentLayout, ...changes });
  };

  const updateRow = (rowIdx, newRow) => {
    const rows = [...currentLayout.rows];
    rows[rowIdx] = newRow;
    update({ rows });
  };

  const addRow = () => {
    const rows = [...currentLayout.rows, { items: [{ field: NONE_VALUE, type: "text" }] }];
    update({ rows });
  };

  const removeRow = (idx) => {
    const rows = currentLayout.rows.filter((_, i) => i !== idx);
    update({ rows });
  };

  const moveRow = (idx, dir) => {
    const rows = [...currentLayout.rows];
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    [rows[idx], rows[target]] = [rows[target], rows[idx]];
    update({ rows });
  };

  const setRowItemCount = (rowIdx, count) => {
    const row = { ...currentLayout.rows[rowIdx] };
    if (count === 1 && row.items.length === 2) {
      row.items = [row.items[0]];
    } else if (count === 2 && row.items.length === 1) {
      row.items = [...row.items, { field: NONE_VALUE, type: "text" }];
    }
    updateRow(rowIdx, row);
  };

  const setRowItemField = (rowIdx, itemIdx, fieldId) => {
    const row = { ...currentLayout.rows[rowIdx] };
    const items = [...row.items];
    const isImage = fieldId === "member_photo";
    items[itemIdx] = { field: fieldId, type: isImage ? "image" : "text" };
    // Keep whatever column count (1 or 2) the row already had — a photo can
    // now share a two-column row with a text field instead of always being
    // forced to take up the full row width.
    row.items = items;
    updateRow(rowIdx, row);
  };

  const backgroundColor = design?.active_color || "#16A34A";
  const textColor = design?.text_color || "#FFFFFF";

  return (
    <div className="space-y-4" data-testid="card-builder">
      {/* Header & Subheader Config */}
      <div className="space-y-3 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Card Header Fields</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-600">Header (Large Text)</Label>
            <Select value={currentLayout.header} onValueChange={(v) => update({ header: v })}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="header-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_OPTIONS.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-600">Subheader</Label>
            <Select value={currentLayout.subheader} onValueChange={(v) => update({ subheader: v })}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="subheader-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>None</SelectItem>
                {FIELD_OPTIONS.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Card Rows Config */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Card Rows</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={addRow}
            disabled={currentLayout.rows.length >= 4}
            data-testid="add-row-btn"
          >
            <Plus size={12} weight="bold" className="mr-1" /> Add Row
          </Button>
        </div>

        {currentLayout.rows.map((row, rowIdx) => {
          return (
            <div
              key={rowIdx}
              className="p-3 bg-zinc-50 rounded-lg border border-zinc-200 space-y-2"
              data-testid={`card-row-${rowIdx}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-500">Row {rowIdx + 1}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveRow(rowIdx, -1)}
                    disabled={rowIdx === 0}
                    className="p-1 rounded hover:bg-zinc-200 disabled:opacity-30"
                    data-testid={`move-row-up-${rowIdx}`}
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    onClick={() => moveRow(rowIdx, 1)}
                    disabled={rowIdx === currentLayout.rows.length - 1}
                    className="p-1 rounded hover:bg-zinc-200 disabled:opacity-30"
                    data-testid={`move-row-down-${rowIdx}`}
                  >
                    <ArrowDown size={12} />
                  </button>
                  <button
                    onClick={() => removeRow(rowIdx)}
                    className="p-1 rounded hover:bg-red-100 text-red-500"
                    data-testid={`remove-row-${rowIdx}`}
                  >
                    <Trash size={12} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <Label className="text-xs text-zinc-500">Columns:</Label>
                <button
                  onClick={() => setRowItemCount(rowIdx, 1)}
                  className={`px-2 py-0.5 text-xs rounded ${row.items.length === 1 ? "bg-[var(--brand)] text-white" : "bg-zinc-200 text-zinc-600"}`}
                >
                  1
                </button>
                <button
                  onClick={() => setRowItemCount(rowIdx, 2)}
                  className={`px-2 py-0.5 text-xs rounded ${row.items.length === 2 ? "bg-[var(--brand)] text-white" : "bg-zinc-200 text-zinc-600"}`}
                >
                  2
                </button>
              </div>

              <div className={`grid gap-2 ${row.items.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
                {row.items.map((item, itemIdx) => (
                  <div key={itemIdx}>
                    <Select
                      value={item.field}
                      onValueChange={(v) => setRowItemField(rowIdx, itemIdx, v)}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`row-${rowIdx}-item-${itemIdx}-select`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>-- Empty --</SelectItem>
                        {FIELD_OPTIONS.map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                        ))}
                        <SelectItem value="member_photo">
                          <span className="flex items-center gap-1">
                            <ImageIcon size={12} /> Member Photo
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Live Mini Preview */}
      <div className="mt-4">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Live Preview</p>
        <div className="rounded-xl overflow-hidden shadow-md" style={{ backgroundColor }}>
          <div className="p-4">
            {/* Logo + Club Name */}
            <div className="flex items-center gap-2 mb-3">
              {design?.logo_url && (
                <img src={design.logo_url} alt="" className="h-8 w-8 rounded-full object-contain bg-white/20 p-0.5"
                  onError={(e) => { e.target.style.display = "none"; }} />
              )}
              <span className="text-xs font-medium opacity-80" style={{ color: textColor }}>
                {design?.club_name || "Club Name"}
              </span>
            </div>

            {/* Subheader */}
            {currentLayout.subheader && currentLayout.subheader !== NONE_VALUE && (
              <p className="text-xs font-semibold mb-0.5" style={{ color: textColor }}>
                {getSampleValue(currentLayout.subheader)} Member
              </p>
            )}

            {/* Header */}
            <p className="text-lg font-bold mb-3" style={{ color: textColor }} data-testid="preview-header">
              {getSampleValue(currentLayout.header) || "Header"}
            </p>

            {/* Rows */}
            {currentLayout.rows.map((row, rowIdx) => (
              <div key={rowIdx} className={`border-t border-white/20 pt-2 pb-1 ${row.items.length === 2 ? "grid grid-cols-2 gap-2" : ""}`}>
                {row.items.map((item, itemIdx) => {
                  if (item.field === NONE_VALUE) return <div key={itemIdx} />;
                  if (item.type === "image") {
                    return (
                      <div key={itemIdx} className="flex justify-center py-1">
                        <div className="w-10 h-10 rounded bg-white/20 flex items-center justify-center">
                          <ImageIcon size={16} style={{ color: textColor }} />
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={itemIdx}>
                      <p className="text-[9px] opacity-50" style={{ color: textColor }}>
                        {getFieldLabel(item.field)}
                      </p>
                      <p className="text-xs font-medium" style={{ color: textColor }}>
                        {getSampleValue(item.field)}
                      </p>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Banner */}
          {design?.banner_image_url && (
            <img src={design.banner_image_url} alt="" className="w-full h-16 object-cover"
              onError={(e) => { e.target.style.display = "none"; }} />
          )}
        </div>
      </div>
    </div>
  );
};

export { DEFAULT_LAYOUT, DATA_FIELDS, NONE_VALUE };
export default CardBuilder;
